import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  ChevronDown, ChevronUp, Activity, ArrowRight, Save, RotateCcw,
  DollarSign, ClipboardList, Loader2, RefreshCw,
} from 'lucide-react';

/**
 * Unified activity timeline for a single appraisal — merges pipeline
 * stage advances, Components + Roof Appraisal saves/restores, and
 * pricing edits/restores into one chronological feed.
 *
 * Sits at the top of the AppraisalDetailPage in a collapsible card so it
 * doesn't dominate the page when the operator just wants to edit data.
 *
 * Props:
 *   appraisalId  string — required
 *   refreshKey   any    — bump from parent after any save / advance /
 *                         pricing edit to force a re-fetch
 *   defaultOpen  bool   — start collapsed by default (false)
 */
export default function AppraisalTimeline({ appraisalId, refreshKey = 0, defaultOpen = false }) {
  const { api } = useAuth();
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(!!defaultOpen);

  const load = useCallback(async () => {
    if (!appraisalId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/appraisals/${appraisalId}/timeline?limit=30`);
      setEvents(data?.events || []);
      setTotal(data?.total || 0);
    } catch {
      setEvents([]); setTotal(0);
    }
    setLoading(false);
  }, [api, appraisalId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Newest event preview for the collapsed header row.
  const newest = events[0];

  return (
    <div data-testid="appraisal-timeline" className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors">
        <button
          type="button"
          data-testid="appraisal-timeline-toggle"
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-3 min-w-0 flex-1 text-left"
        >
          <div className="p-1.5 rounded-md bg-orange-50">
            <Activity size={14} className="text-[#D35400]" strokeWidth={2.5} />
          </div>
          <div className="text-left min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Activity Timeline</p>
            <p className="text-xs text-slate-700 truncate">
              {loading ? 'Loading…' :
                newest ? (
                  <>
                    <span className="font-semibold">{newest.title}</span>
                    <span className="text-slate-400"> · </span>
                    <span className="text-slate-500">{newest.detail}</span>
                    <span className="text-slate-400 ml-1">({relTs(newest.ts)})</span>
                  </>
                ) : <span className="italic text-slate-400">No activity yet — first edit will show up here.</span>
              }
            </p>
          </div>
        </button>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 font-mono-num">
            {total} event{total === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            data-testid="appraisal-timeline-refresh"
            onClick={load}
            className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"
            title="Refresh"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            className="p-1 rounded hover:bg-slate-100 text-slate-400"
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="appraisal-timeline-body" className="border-t border-slate-100 max-h-80 overflow-y-auto">
          {loading && events.length === 0 ? (
            <div className="text-center py-6 text-xs text-slate-400 flex items-center justify-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Loading timeline…
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-6 text-xs text-slate-400 italic">
              No activity recorded yet. As soon as you save, advance the stage, or edit pricing, entries will appear here.
            </div>
          ) : (
            <ol className="divide-y divide-slate-50">
              {events.map((e, i) => (
                <TimelineRow key={`${e.ref_id || e.ts}-${i}`} e={e} />
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}


function TimelineRow({ e }) {
  const { Icon, accent, accentBg } = iconForEvent(e);
  return (
    <li className="flex items-start gap-3 px-4 py-2.5 hover:bg-amber-50/30 transition-colors" data-testid={`timeline-event-${e.kind}-${e.action}`}>
      <div className={`p-1.5 rounded-md ${accentBg} mt-0.5 flex-shrink-0`}>
        <Icon size={11} className={accent} strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-slate-800 truncate">{e.title}</p>
        {e.detail && <p className="text-[11px] text-slate-500 truncate" title={e.detail}>{e.detail}</p>}
      </div>
      <div className="text-right flex-shrink-0 ml-2">
        <p className="text-[10px] font-mono-num font-semibold text-slate-600 whitespace-nowrap">{relTs(e.ts)}</p>
        <p className="text-[10px] text-slate-400 truncate max-w-[120px]">{e.user_name}</p>
      </div>
    </li>
  );
}


function iconForEvent(e) {
  if (e.kind === 'stage') return { Icon: ArrowRight, accent: 'text-emerald-600', accentBg: 'bg-emerald-50' };
  if (e.kind === 'pricing') return { Icon: DollarSign, accent: 'text-amber-600', accentBg: 'bg-amber-50' };
  if (e.kind === 'form' && (e.action || '').startsWith('restore')) {
    return { Icon: RotateCcw, accent: 'text-purple-600', accentBg: 'bg-purple-50' };
  }
  if (e.kind === 'form' && e.scope === 'conditions') {
    return { Icon: ClipboardList, accent: 'text-blue-600', accentBg: 'bg-blue-50' };
  }
  return { Icon: Save, accent: 'text-slate-600', accentBg: 'bg-slate-100' };
}


function relTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const diffSec = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return `${Math.round(diffSec)}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86400) return `${Math.round(diffSec / 86400)}d ago`;
  return d.toLocaleString('en-NZ', { day: '2-digit', month: 'short', year: '2-digit' });
}
